import { z } from 'zod';

import { billingCycleSchema } from '../plan.schema';
import { discountTypeSchema } from '../subscription.schema';
import { todayLocal } from '../../utils/datetime';

// =============================================================================
//  CRM Proposal schemas — Story 11.1 (fundação) + Story 11.6 (CRUD + ciclo + numeração)
//  Fonte única back+front (arquitetura 08-fase3-crm-architecture.md § III.4, FR-52).
//  Reuso: BillingCycle, DiscountType (DR-024/DR-028).
//  Numeração (PROP-{ano}-{seq}) gerada no service; envio/link público e conversão
//  ficam em 11.7/11.9. proposal_number, public_token e converted_subscription_id
//  são gerados pelo service (não vêm no payload de criação) — fora do createSchema.
// =============================================================================

const YMD_REGEX = /^\d{4}-\d{2}-\d{2}$/;

export const crmProposalStatusSchema = z.enum([
  'rascunho',
  'enviada',
  'visualizada',
  'aceita',
  'recusada',
  'expirada',
  'convertida',
]);
export type CrmProposalStatusSchema = z.infer<typeof crmProposalStatusSchema>;

// Valor monetário com até 2 casas; quando informado deve ser > 0 (AC1 — "valor
// proposto > 0"). Opcional/nullable: proposta pode não ter um dos componentes
// (ex: só mensal, sem setup). Limite Decimal(10,2).
const moneyField = z
  .number()
  .positive('Valor deve ser maior que zero.')
  .max(99_999_999.99, 'Valor excede o limite.')
  .multipleOf(0.01, 'Valor deve ter no máximo 2 casas decimais.')
  .optional()
  .nullable();

/**
 * Refine compartilhado (create + update): consistência do desconto (DR-024).
 * Se há `discount_amount`, `discount_reason` e `discount_type` são obrigatórios.
 * Aplica só quando `discount_amount` está presente no payload (no update parcial,
 * ausência = "não mexe", então não exige os companheiros).
 */
function refineDiscountConsistency(
  data: {
    discount_amount?: number | null;
    discount_type?: 'percent' | 'fixed' | null;
    discount_reason?: string | null;
  },
  ctx: z.RefinementCtx,
): void {
  if (data.discount_amount === undefined || data.discount_amount === null) return;
  if (
    data.discount_reason === undefined ||
    data.discount_reason === null ||
    data.discount_reason.trim() === ''
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['discount_reason'],
      message: 'Motivo do desconto é obrigatório quando há desconto.',
    });
  }
  if (data.discount_type === undefined || data.discount_type === null) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['discount_type'],
      message: 'Tipo do desconto é obrigatório quando há desconto.',
    });
  }
}

// -----------------------------------------------------------------------------
// CREATE — saas_product_id, plan_id e valid_until obrigatórios (§ III.4 / AC1).
// `valid_until` deve ser >= hoje (sem proposta nascendo vencida).
// -----------------------------------------------------------------------------

const createCrmProposalBaseSchema = z.object({
  deal_id: z.string().uuid('deal_id inválido.').optional().nullable(),
  lead_id: z.string().uuid('lead_id inválido.').optional().nullable(),
  client_id: z.string().uuid('client_id inválido.').optional().nullable(),
  saas_product_id: z.string().uuid('saas_product_id inválido.'),
  plan_id: z.string().uuid('plan_id inválido.'),
  billing_cycle: billingCycleSchema.default('mensal'),
  monthly_value: moneyField,
  annual_value: moneyField,
  setup_value: moneyField,
  discount_amount: moneyField,
  discount_type: discountTypeSchema.optional().nullable(),
  discount_reason: z
    .string()
    .trim()
    .max(500, 'Motivo deve ter até 500 caracteres.')
    .optional()
    .nullable(),
  valid_until: z
    .string()
    .regex(YMD_REGEX, 'Data deve estar no formato YYYY-MM-DD')
    .refine((d) => d >= todayLocal(), 'Validade não pode ser uma data passada.'),
  observations: z
    .string()
    .max(5000, 'Observações devem ter até 5000 caracteres.')
    .optional()
    .nullable(),
});

export const createCrmProposalSchema =
  createCrmProposalBaseSchema.superRefine(refineDiscountConsistency);
export type CreateCrmProposalDto = z.output<typeof createCrmProposalSchema>;
export type CreateCrmProposalInput = z.input<typeof createCrmProposalSchema>;

// -----------------------------------------------------------------------------
// UPDATE — só em rascunho (regra de status no service da 11.6); campos opcionais.
// `valid_until` (se enviado) ainda precisa ser >= hoje. `discount_amount` ainda
// exige reason+type. `deal_id`/`lead_id`/`client_id` não são re-editáveis aqui
// (o vínculo nasce na criação); por isso o update foca nos campos de conteúdo.
// -----------------------------------------------------------------------------

const updateCrmProposalBaseSchema = createCrmProposalBaseSchema
  .partial()
  .extend({
    version: z.coerce.number().int().positive('version é obrigatório (optimistic lock).'),
  })
  .strict();

export const updateCrmProposalSchema =
  updateCrmProposalBaseSchema.superRefine(refineDiscountConsistency);
export type UpdateCrmProposalDto = z.output<typeof updateCrmProposalSchema>;
export type UpdateCrmProposalInput = z.input<typeof updateCrmProposalSchema>;

// -----------------------------------------------------------------------------
// QUERY — cursor pagination (DR-032) + filtros (AC4): status, cliente/lead/deal,
// saas, responsável (via deal.responsibleUserId), "a vencer em N dias", período.
// -----------------------------------------------------------------------------

// -----------------------------------------------------------------------------
// REJECT — recusa de proposta (Story 11.7 AC4). Motivo obrigatório (min 5 chars)
// pra registrar contexto da recusa na atividade/audit. Limite alinhado aos demais
// campos de texto curto da proposta.
// -----------------------------------------------------------------------------

export const rejectCrmProposalSchema = z
  .object({
    rejection_reason: z
      .string()
      .trim()
      .min(5, 'Motivo da recusa deve ter ao menos 5 caracteres.')
      .max(500, 'Motivo da recusa deve ter até 500 caracteres.'),
  })
  .strict();
export type RejectCrmProposalDto = z.output<typeof rejectCrmProposalSchema>;
export type RejectCrmProposalInput = z.input<typeof rejectCrmProposalSchema>;

// -----------------------------------------------------------------------------
// CONVERT — converte proposta aceita → assinatura (Story 11.9 / FR-53).
//
// Body opcional `client_data`: completa os dados do Client quando a proposta
// está vinculada só a um lead (que não tem documento) e o plano gera billing
// imediato (documento obrigatório — DR-012). Se a proposta já tem `client_id` ou
// o lead já virou cliente, esses campos são ignorados. `payment_method` é
// opcional (repassado ao SubscriptionsService). Nenhum dado é inventado: o que
// faltar e for obrigatório retorna 400 MISSING_CLIENT_DATA.
// -----------------------------------------------------------------------------

const convertClientDataSchema = z
  .object({
    type: z.enum(['pessoa_fisica', 'pessoa_juridica']).optional(),
    document: z.string().trim().min(1).max(30).optional().nullable(),
    document_type: z.enum(['CPF', 'CNPJ', 'PASSPORT', 'OTHER']).optional(),
    email: z
      .union([z.string().email('Email inválido.'), z.literal('')])
      .optional()
      .nullable(),
    phone: z.string().max(20).optional().nullable(),
  })
  .strict();
export type ConvertProposalClientData = z.output<typeof convertClientDataSchema>;

export const convertCrmProposalSchema = z
  .object({
    /** Forma de pagamento da assinatura (opcional — repassada ao billing). */
    payment_method: z
      .enum(['pix', 'boleto', 'cartao', 'transferencia', 'cobranca_manual'])
      .optional()
      .nullable(),
    /** Dados pra completar o Client na promoção lead→Client (quando necessário). */
    client_data: convertClientDataSchema.optional(),
  })
  .strict();
export type ConvertCrmProposalDto = z.output<typeof convertCrmProposalSchema>;
export type ConvertCrmProposalInput = z.input<typeof convertCrmProposalSchema>;

export const convertCrmProposalResponseSchema = z.object({
  data: z.object({
    subscriptionId: z.string().uuid(),
    clientId: z.string().uuid(),
    proposalNumber: z.string(),
    /** true quando esta chamada criou a assinatura; false quando já existia (idempotente). */
    created: z.boolean(),
  }),
});
export type ConvertCrmProposalResponse = z.infer<typeof convertCrmProposalResponseSchema>;

// -----------------------------------------------------------------------------
// EXPIRING QUERY — propostas a vencer (Story 11.7 AC8). `days` default 7.
// -----------------------------------------------------------------------------

export const expiringCrmProposalsSchema = z
  .object({
    days: z.coerce.number().int().min(0).max(365).default(7),
  })
  .strict();
export type ExpiringCrmProposalsDto = z.output<typeof expiringCrmProposalsSchema>;

export const queryCrmProposalsSchema = z
  .object({
    status: crmProposalStatusSchema.optional(),
    client_id: z.string().uuid('client_id inválido.').optional(),
    lead_id: z.string().uuid('lead_id inválido.').optional(),
    deal_id: z.string().uuid('deal_id inválido.').optional(),
    saas_product_id: z.string().uuid('saas_product_id inválido.').optional(),
    /** Responsável pela proposta = responsável do deal vinculado (AC4). */
    responsible_user_id: z.string().uuid('responsible_user_id inválido.').optional(),
    /** Propostas com `valid_until` entre hoje e hoje+N dias (a vencer — AC4). */
    expiring_days: z.coerce.number().int().min(0).max(365).optional(),
    created_from: z.string().regex(YMD_REGEX, 'created_from deve ser YYYY-MM-DD').optional(),
    created_to: z.string().regex(YMD_REGEX, 'created_to deve ser YYYY-MM-DD').optional(),
    cursor: z.string().uuid('cursor inválido.').optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
  })
  .strict()
  .superRefine((data, ctx) => {
    if (data.created_from && data.created_to && data.created_from > data.created_to) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['created_from'],
        message: 'created_from não pode ser maior que created_to.',
      });
    }
  });
export type QueryCrmProposalsDto = z.output<typeof queryCrmProposalsSchema>;
export type QueryCrmProposalsInput = z.input<typeof queryCrmProposalsSchema>;

// -----------------------------------------------------------------------------
// PUBLIC VIEW — subset exibido no link público read-only (Story 11.7 AC2).
// SEM PII desnecessária e SEM `public_token` no payload (evita vazar o token via
// response). Valores monetários como string (convenção de dinheiro do projeto).
// Schema usado também pelo frontend público pra validar a resposta (Tier 2).
// -----------------------------------------------------------------------------

export const proposalPublicViewSchema = z.object({
  proposal_number: z.string(),
  status: crmProposalStatusSchema,
  billing_cycle: billingCycleSchema,
  monthly_value: z.string().nullable(),
  annual_value: z.string().nullable(),
  setup_value: z.string().nullable(),
  discount_amount: z.string().nullable(),
  discount_type: discountTypeSchema.nullable(),
  valid_until: z.string(),
  observations: z.string().nullable(),
  saas_product: z.object({ name: z.string() }),
  plan: z.object({ name: z.string() }),
});
export type ProposalPublicView = z.infer<typeof proposalPublicViewSchema>;
